import React, {useEffect} from 'react';
import TwoColumnPageContent from "../../hoc/TwoColumnPageContent/TwoColumnPageContent";
import StateType from "../../store/store";
import {connect, ConnectedProps} from "react-redux";
import PageTitle from "../../components/PageTitle/PageTitle";
import AccountActions from "../../store/actions/AccountActions";
import {Col, Row, Spinner, Image} from "react-bootstrap";
import { Helmet } from 'react-helmet';

import styles from './PrivatePolicy.module.scss';

const map_state_to_props = (state: StateType) => ({
    state: {
        is_admin: state.account.is_admin,
    }
});

const map_dispatch_to_props = (dispatch: any) => ({
    actions: {
        go_home: () => dispatch(AccountActions.go_home())
    }
});

const connector = connect(map_state_to_props, map_dispatch_to_props);

type PrivatePolicyProps = ConnectedProps<typeof connector> & {};

const PrivatePolicy = ({
                      state: {
                          is_admin,
                      },
                      actions: {
                          go_home,
                      }
                  }: PrivatePolicyProps) => {

    const PrivatePolicy = () => (
        <div className={styles.section}>
            <h3 className={`fw-bold mb-4`}>Private Policy</h3>
            <p>
                The Big Wave Systems, LLC, product, Consent On Demand, is committed to respecting the privacy rights and concerns of
                all users of the platform where this Privacy Policy is posted (the “Site”), and for all services made available by us 
                (collectively with the Site, the “Service ”). As such, we have established and implemented this Privacy Policy to inform visitors 
                to the Site and users of the Service how we use and protect the information we collect. By accessing the Site or using the Service, 
                you consent to our collection, use, storage, deletion, and disclosure of information relating to you as set forth in this Privacy Policy. 
                This Privacy Policy is effective as of the date set forth above and is only applicable to the Service and not to any other website that you 
                may be able to access from the Service which may have its own data collection and use practices and policies.
            </p>
        </div>
    );

    const InformationCollection = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Information Collection:</h5>
            <p className={'mb-3'}>
                By submitting personal information through the Service, you authorize us to share this personal information for the
                purposes identified herein, such as responding to user inquiries, processing transactions, providing services, and requesting feedback.
            </p>
            <p>
                We may also collect non-identifying personal information such as your browser type, domain names, mobile device operating system,
                when you accessed the Service and from what location you access the Service. This information may be aggregated to measure the number
                of visits, average time spent, page views, and other statistics about users of the Service. We may use this data to monitor the Service’s 
                performance and to make the Service easier and more convenient to use.
            </p>
        </div>
    );

    const PersonalInformationCollected = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Personal Information Collected:</h5>
            <ul style={{ listStyleType: 'disc' }}>
                <li>Name</li>
                <li>Phone Number</li>
                <li>Email Address</li>
                <li>Location</li>
                <li>IP Address</li>
                <li>Digital signature</li>
            </ul>
        </div>
    );

    const CompanyInformationCollected = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Company Information Collected:</h5>
            <ul style={{ listStyleType: 'disc' }} className={'mb-3'}>
                <li>Company Name</li>
                <li>Company Address</li>
            </ul>
            <p className={'mb-3'}>
                The personal information you provide to us will allow us to communicate with you and provide the Service to you,
                depending on the aspects of the Service you utilize. This includes, but not limited to, connecting you to an insurance broker; 
                customer service questions or issues; verifying your identity; and requesting feedback and upon your consent. We may also use 
                your email address as part of the Service in sending you messages about the Service and other general announcements. We keep track 
                of your interactions with us, including but not limited to your activity within the Service. Except as set forth in this 
                Privacy Policy, or unless we have your consent, we will not share your personal information with any person or entity other 
                than those affiliated with us, entities acting on our behalf, and relevant third parties such as those needed to collect 
                and maintain our servers and perform technology and related services.
            </p>
            <p className={'mb-3'}>
                We may use your personal information with non-personal information that we have collected in a manner
                such that the end-product does not personally identify you or any other user of the Service. We may make your
                personal information non-personally identifiable by either combining it with information about other users 
                (aggregating your personal information with information about other users), or by removing characteristics 
                (such as your name or email address) that make the information personally identifiable. This process is known as 
                de-personalizing your information. You grant us a royalty-free, worldwide, perpetual, irrevocable and fully transferable 
                right and license to use your personal information in connection with the creation and development of analytical and statistical 
                analysis tools relating to the use of the customer data we collect in providing the Service (the “Analytical Data”). We are expressly 
                authorized to make any commercial use of the Analytical Data, including without limitation, supplementing our valuation products, 
                sharing the Analytical Data with third parties, and otherwise exploiting the Analytical Data, if we do not sell, trade, or otherwise 
                transfer from us any part of the Analytical Data that personally identifies any Service users.
            </p>
        </div>
    );

    const DataSecurity = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Data Security:</h5>
            <ul style={{ listStyleType: 'disc' }}>
                <li>We implement security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.</li>
                <li>Our security measures include encryption protocols to safeguard your personal information during transmission.</li>
                <li>Regular security assessments and audits are conducted to identify and address potential vulnerabilities.</li>
                <li>Access to personal information is restricted to authorized personnel with a need to know, and strict access controls are enforced.</li>
                <li>We employ industry-standard firewalls and intrusion detection systems to prevent unauthorized access to our systems.</li>
                <li>Data backups are performed regularly to minimize the risk of data loss in the event of unforeseen circumstances.</li>
                <li>While we take all reasonable precautions, no method of transmission over the internet or electronic storage is completely secure; therefore, we cannot guarantee absolute security.</li>
                <li>Users are encouraged to report any security concerns or incidents promptly to our dedicated security contact.</li>      
            </ul>
        </div>
    );

    const ThirdPartyServices = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Third-Party Services:</h5>
            <ul style={{ listStyleType: 'disc' }}>
                <li>We may use third-party services to facilitate our Service and may share your information with them for that purpose.</li>
            </ul>
        </div>
    );

    const Cookies = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Cookies:</h5>
            <ul style={{ listStyleType: 'disc' }}>
                <li>We use cookies to enhance your experience while using the Service.</li>
                <li>The software may use session cookies for temporary storage of information during a user's session and persistent cookies for remembering user preferences.</li>
                <li>Users have the option to manage cookie preferences through their browser settings.</li>
                <li>Cookies used by our software are secure and do not store sensitive personal information.</li>
                <li>Changes to our cookie usage will be reflected in our Cookie Policy, and users will be notified of any significant updates.</li>
            </ul>
        </div>
    );

    const ChangesToPrivacyPolicy = () => (
        <div className={styles.section}>
            <h5 className={`fw-bold`}>Changes to Privacy Policy:</h5>
            <ul style={{ listStyleType: 'disc' }} className='mb-3'>
                <li>We reserve the right to update or change this Privacy Policy at any time.</li>
            </ul>
            <h5 className={`fw-bold`}>By using Consent On Demand, you agree to the terms of this Privacy Policy.</h5>
        </div>
    );


    return (
        <Col xs={12}>
            <Row className={'mt-4 mt-md-4 d-flex justify-content-center'}>
                <Col xs={12} md={{span: 5}} lg={{span: 3}} >
                    <Image src={'/images/Consent-On-Demand-logo-CMYK-300.jpg'}
                        alt={'Consent on Demand Logo'}
                        fluid
                        className={'w-100 me-auto ms-auto'}/>
                </Col>
            </Row>
            <Col className={`${styles.Disclosure} mt-3`}>
                <Col xs={12} lg={{span: 12}} className={'mb-5 mb-sm-0'}>
                    <Row>
                        <PrivatePolicy />
                        <InformationCollection />
                        <PersonalInformationCollected />
                        <CompanyInformationCollected />
                        <DataSecurity />
                        <ThirdPartyServices />
                        <Cookies />
                        <ChangesToPrivacyPolicy />
                    </Row>
                </Col>
            </Col>
        </Col>
    );
};

export default connector(PrivatePolicy);
